/**
 * Shared domain types for Course Management, mirroring the backend DTOs in
 * `hub-plus-api` under
 * `App\Module\Hub\Feature\LearningCenter\DTO\{Request,Response}`.
 *
 * The backend response envelope is always `{ data, meta }`.
 */

export type CourseStatus = "draft" | "published" | "archived";

/**
 * Content block types accepted by the backend
 * (`CourseContentBlock::TYPES`). Kept in sync with the server-side validation.
 * `text` is a rich (WYSIWYG) block.
 */
export const COURSE_CONTENT_BLOCK_TYPES = [
  "text",
  "image",
  "file",
  "video",
  "callout",
  "reflection",
  "knowledge_check",
] as const;

export type CourseContentBlockType =
  (typeof COURSE_CONTENT_BLOCK_TYPES)[number];

/** Mirrors `TaxonomyTermResponse` (id is an int, including for trades). */
export interface TaxonomyTerm {
  id: number;
  name: string;
}

/** Mirrors `CourseContentBlockResponse`. */
export interface CourseContentBlock {
  uuid: string;
  type: CourseContentBlockType;
  content: string | null;
  orderNumber: number;
  metadata: Record<string, unknown> | null;
}

/** Mirrors `CourseModuleResponse`. */
export interface CourseModule {
  uuid: string;
  title: string;
  description: string | null;
  difficulty: string | null;
  estimatedMinutes: number | null;
  tags: string[] | null;
  orderNumber: number;
  blocks: CourseContentBlock[];
}

/** Mirrors `CourseEnrollmentStatsResponse`. */
export interface CourseEnrollmentStats {
  totalEnrolled: number;
  completedCount: number;
  completionRate: number;
}

/** Mirrors `CourseResponse` (the full course graph). */
export interface Course {
  uuid: string;
  title: string;
  tagline: string | null;
  description: string | null;
  level: string | null;
  estimatedHours: string | null;
  targetAudience: string | null;
  learningObjectives: string[];
  courseOverview: string | null;
  status: CourseStatus;
  publishedAt: string | null;
  thumbnailUrl: string | null;
  heroBannerUrl: string | null;
  /**
   * The stored File UUIDs behind the images. The edit form round-trips these —
   * it posts both on every save, so they must be hydrated or the untouched
   * image gets wiped.
   */
  thumbnailFileUuid: string | null;
  heroBannerFileUuid: string | null;
  modules: CourseModule[];
  trades: TaxonomyTerm[];
  enrollmentStats: CourseEnrollmentStats;
}

/** Mirrors `CourseListItemResponse`. */
export interface CourseListItem {
  uuid: string;
  title: string;
  tagline: string | null;
  level: string | null;
  status: CourseStatus;
  estimatedHours: string | null;
  moduleCount: number;
  totalEnrolled: number;
  completionRate: number;
  thumbnailUrl: string | null;
  publishedAt: string | null;
}

/** Mirrors `CourseMetadataResponse`. */
export interface CourseMetadata {
  /** The available course levels, e.g. Foundational/Intermediate/Advanced/Expert. */
  levels: string[];
  trades: TaxonomyTerm[];
  statuses: { value: string; label: string }[];
}

/** Mirrors `CourseContentBlockRequest`. */
export interface CourseContentBlockPayload {
  type: CourseContentBlockType;
  content?: string | null;
  orderNumber: number;
  metadata?: Record<string, unknown> | null;
  /** Present when editing an existing block; omitted for new blocks. */
  uuid?: string | null;
}

/** Mirrors `CourseModuleRequest`. */
export interface CourseModulePayload {
  title: string;
  description?: string | null;
  difficulty?: string | null;
  estimatedMinutes?: number | null;
  tags?: string[] | null;
  orderNumber: number;
  blocks: CourseContentBlockPayload[];
  /** Present when editing an existing module; omitted for new modules. */
  uuid?: string | null;
  /**
   * When set on a NEW module (no uuid), the server copies the published
   * library module with this uuid into the course as an independent snapshot.
   */
  sourceLibraryModuleUuid?: string | null;
}

/* -------------------------------------------------------------------------- */
/* Interactive content block metadata                                         */
/*                                                                            */
/* The wire type for a block's `metadata` is an open `Record<string,unknown>` */
/* (opaque to the backend). The interfaces below describe the agreed payload  */
/* shapes for the richer block types, and the parse/guard helpers narrow the  */
/* open bag into those shapes — tolerating malformed/legacy data with sane    */
/* defaults so the renderer never throws.                                     */
/* -------------------------------------------------------------------------- */

/** Default heading shown for a callout when no label was authored. */
export const DEFAULT_CALLOUT_LABEL = "Note";

/** `callout` metadata. The body markdown lives in the block's `content`. */
export interface CalloutMeta {
  label: string;
}

/** `reflection` metadata. `content` is null for reflection blocks. */
export interface ReflectionMeta {
  prompts: string[];
}

/**
 * Question types supported by a knowledge check.
 *
 * v1 ships `single` (multiple choice, exactly one correct) and `true_false`.
 * The list is intentionally a const tuple so it can grow (multi-select,
 * short-text, likert, …) without reshaping the surrounding types.
 */
export const KNOWLEDGE_CHECK_QUESTION_TYPES = ["single", "true_false"] as const;

export type KnowledgeCheckQuestionType =
  (typeof KNOWLEDGE_CHECK_QUESTION_TYPES)[number];

/** A single answer option within a knowledge-check question. */
export interface KnowledgeCheckOption {
  id: string;
  label: string;
  /** Marks the correct option. Exactly one option is correct for v1 types. */
  correct?: boolean;
}

/** A single knowledge-check question. */
export interface KnowledgeCheckQuestion {
  id: string;
  type: KnowledgeCheckQuestionType;
  prompt: string;
  explanation?: string;
  options: KnowledgeCheckOption[];
}

/** `knowledge_check` metadata. `content` is null for these blocks. */
export interface KnowledgeCheckContent {
  version: 2;
  title?: string;
  questions: KnowledgeCheckQuestion[];
}

/**
 * Narrow an open metadata bag into `CalloutMeta`. Falls back to
 * {@link DEFAULT_CALLOUT_LABEL} when the label is missing or not a usable
 * string. Never throws.
 */
export function parseCalloutMeta(
  metadata: Record<string, unknown> | null | undefined,
): CalloutMeta {
  const label = metadata?.label;
  return {
    label:
      typeof label === "string" && label.trim() !== ""
        ? label
        : DEFAULT_CALLOUT_LABEL,
  };
}

/**
 * Narrow an open metadata bag into `ReflectionMeta`. Non-string prompt entries
 * (and a non-array `prompts`) are dropped. Never throws.
 */
export function parseReflectionMeta(
  metadata: Record<string, unknown> | null | undefined,
): ReflectionMeta {
  const raw = metadata?.prompts;
  const prompts = Array.isArray(raw)
    ? raw.filter((p): p is string => typeof p === "string")
    : [];
  return { prompts };
}

/** Mirrors `CreateCourseRequest` / `UpdateCourseRequest` (identical shape). */
export interface CoursePayload {
  title: string;
  tagline?: string | null;
  description?: string | null;
  level?: string | null;
  estimatedHours?: string | null;
  targetAudience?: string | null;
  learningObjectives: string[];
  courseOverview?: string | null;
  thumbnailFileUuid?: string | null;
  heroBannerFileUuid?: string | null;
  modules: CourseModulePayload[];
  tradeIds: number[];
}
